import { useQueryClient } from "@tanstack/react-query"
import { Languages, Moon, Sun } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { currentLang, setLanguage } from "@/lib/i18n"
import { setTheme, useTheme } from "@/lib/theme"
import { cn } from "@/lib/utils"

/** Switches between the light and dark design. */
export function ThemeToggle({ className, size = "icon-sm" }: { className?: string; size?: "icon" | "icon-sm" }) {
  const { t } = useTranslation()
  const theme = useTheme()
  const next = theme === "dark" ? "light" : "dark"
  const label = t(next === "dark" ? "theme.toDark" : "theme.toLight")
  return (
    <Button
      variant="ghost"
      size={size}
      className={cn(className)}
      aria-label={label}
      title={label}
      onClick={() => setTheme(next)}
    >
      {theme === "dark" ? <Sun /> : <Moon />}
    </Button>
  )
}

/** Switches Arabic ↔ English; server-provided labels are refetched in the new language. */
export function LanguageToggle({ className, compact = false }: { className?: string; compact?: boolean }) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const next = currentLang() === "ar" ? "en" : "ar"
  return (
    <Button
      variant="ghost"
      size={compact ? "icon-sm" : "sm"}
      className={cn(className)}
      lang={next}
      aria-label={t("language.switchLabel")}
      title={t("language.switchLabel")}
      onClick={async () => {
        await setLanguage(next)
        await qc.invalidateQueries()
      }}
    >
      {compact ? (
        <Languages />
      ) : (
        <>
          <Languages /> {t("language.switch")}
        </>
      )}
    </Button>
  )
}
